<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import Form from '../../common/form/Form.vue';
import FormSaveBtn from '../../common/form/FormSaveBtn.vue';

const props = defineProps({
    modal: { type: Object, required: true },
    options: { type: Object, required: true },
});

const emit = defineEmits(['hide-modal']);

const { proxy } = getCurrentInstance();
const store = proxy.$store;
const router = proxy.$router;

// --- State ---

const id = ref(props.options.id || 0);
const modalTitle = ref('');

// --- Computed ---

const tableName = computed(() => store.state.themes.tableName);
const sectionName = computed(() => store.state.themes.sectionName);
const formId = computed(() => `form:${tableName.value}:${id.value}`);

/**
 * Check if the specific Vuex form module has been registered.
 * This prevents the "clickedAnchor" and "tableName" undefined errors.
 */
const formExists = computed(() => {
    return !!store.state[formId.value];
});

// --- Methods ---

const getType = () => {
    const types = {
        page_templates: 'page',
        page_sections: 'section',
        page_containers: 'container',
        content_types: 'contentType'
    };
    return types[sectionName.value] || '';
};

const setModalTitle = () => {
    const titles = {
        themes_css: 'CSS',
        themes_javascript: 'script',
        content_types: 'content type',
        page_templates: 'page template',
        page_sections: 'page section',
        page_containers: 'page container',
        templates_templates: 'template'
    };
    const prefix = id.value ? 'Edit ' : 'New ';
    modalTitle.value = prefix + (titles[sectionName.value] || 'item');
};

const createForm = () => {
    const fieldNames = ['title'];

    switch (sectionName.value) {
        case 'page_templates':
            fieldNames.push('cssGroupName');
            break;
        case 'content_types':
            fieldNames.push(
                'contentType', 'componentId', 'filterAttribute',
                'lazyLoad', 'pageEditorGroup'
            );
            break;
        case 'themes_css':
            fieldNames.push('mode', 'groups');
            break;
        default:
            fieldNames.push('pageHeader', 'pageFooter');
    }

    // eslint-disable-next-line vue/no-mutating-props
    props.modal.formId = formId.value;

    store.dispatch('forms/createForm', {
        tableName: tableName.value,
        id: id.value,
        fieldNames: fieldNames,
        currentData: {
            type: getType()
        },
        onInit: () => {
            const formState = store.state[formId.value];
            if (formState?.fields?.length > 0) {
                formState.fields[0].autofocus = true;
            }
        },
        onSave: (obj) => {
            if (!id.value) {
                store.dispatch('themes/setItems');
                router.push({
                    name: 'themesEditor',
                    query: { section: sectionName.value, id: obj.id }
                });

                if (sectionName.value === 'content_types') {
                    store.dispatch('components/init');
                }
            }

            setTimeout(() => {
                emit('hide-modal');
            }, 100);
        }
    });
};

const deleteItem = () => {
    store.dispatch('themes/deleteItem', {
        router: router,
        id: id.value,
        onSuccess: () => {
            emit('hide-modal');
        }
    });
};

// --- Lifecycle ---

onMounted(() => {
    createForm();
    setModalTitle();
});
</script>

<template>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{{ modalTitle }}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    @click="$emit('hide-modal')"
                ></button>
            </div>

            <div class="modal-body">
                <Form v-if="formExists" :id="id" :form-id="formId"/>

                <div v-else class="p-5 text-center">
                    <div class="spinner-border text-secondary" role="status"></div>
                </div>
            </div>

            <div class="modal-footer d-flex justify-content-between">
                <div>
                    <button
                        v-if="id"
                        class="btn btn-outline-danger"
                        type="button"
                        @click.prevent="deleteItem"
                    >
                        <i class="bi-trash3 me-1"/> Delete
                    </button>
                </div>

                <FormSaveBtn v-if="formExists" :form-id="formId"/>
            </div>
        </div>
    </div>
</template>